/**
 *
 * Transformation presets supported by ImageProxy service (cw-img-proxy)
 *
 * @see https://github.com/catawiki/cw-img-proxy/blob/master/presets
 * @see https://docs.imgproxy.net/generating_the_url?id=preset
 */
export var ImageTransformationPreset;
(function (ImageTransformationPreset) {
    ImageTransformationPreset["REFERENCE"] = "cw_reference";
    ImageTransformationPreset["NORMAL"] = "cw_normal";
    ImageTransformationPreset["THUMB_1"] = "cw_thumb1";
    ImageTransformationPreset["THUMB_2"] = "cw_thumb2";
    ImageTransformationPreset["THUMB_3"] = "cw_thumb3";
    ImageTransformationPreset["THUMB_4"] = "cw_thumb4";
    ImageTransformationPreset["THUMB_5"] = "cw_thumb5";
    ImageTransformationPreset["THUMB_6"] = "cw_thumb6";
    ImageTransformationPreset["LDP_THUMB"] = "cw_ldp_thumb";
    ImageTransformationPreset["LARGE"] = "cw_large";
    ImageTransformationPreset["LOT_CARD_EXT"] = "cw_lot_card_ext";
    ImageTransformationPreset["LOT_CARD"] = "cw_lot_card";
    ImageTransformationPreset["LOT_CARD_M"] = "cw_lot_card_m";
    ImageTransformationPreset["AUCTION_LARGE"] = "cw_auction_large";
    ImageTransformationPreset["AUCTION_LARGE_M"] = "cw_auction_large_m";
    ImageTransformationPreset["AUCTION_SMALL"] = "cw_auction_small";
    ImageTransformationPreset["AUCTION_SMALL_M"] = "cw_auction_small_m";
    ImageTransformationPreset["ORDER"] = "cw_order";
    ImageTransformationPreset["COLLECTION_CARD_PORTRAIT"] = "cw_collection_card_portrait";
    ImageTransformationPreset["COLLECTION_CARD_LANDSCAPE"] = "cw_collection_card_landscape";
    ImageTransformationPreset["GRAVITY_SMART"] = "cw_grav_sm";
    ImageTransformationPreset["GRAVITY_CENTER"] = "cw_grav_ce";
})(ImageTransformationPreset || (ImageTransformationPreset = {}));
/**
 * @ignore
 */
const createPresetProcessor = (presets, dpr) => {
    const prs = dpr && dpr > 1 ? [...presets, 'cw_dpr2'] : presets;
    return prs.join(':');
};
/**
 * @ignore
 */
const createProcessorQuery = (options) => {
    if (typeof options === 'string') {
        return createPresetProcessor([options]);
    }
    return createPresetProcessor(options.presets, options.dpr);
};
/**
 *
 * Transforms the lot image url using the ImageProxy service (cw-img-proxy).
 * options parameters can be either a known transformation preset or the object
 * that consists of the list of transformations plus width and height
 *
 * @example
 * import { Image } from '@catawiki/runtime-toolkit';
 *
 * const imageProxyHost = 'https://assets.catawiki.com';
 * const imageUrl = 'https://assets.catawiki.nl/assets/2021/5/3/a/c/9/thumb3_ac957456-7239-469e-afbb-01c44214492d.jpg';
 * assert.strictEqual(Image.transformImageUrl(imageProxyHost, imageUrl, {
 *           presets: [Image.ImageTransformationPreset.LARGE],
 *           dpr: 2
 *        }), 'https://assets.catawiki.com/image/cw_large:cw_dpr2/plain/assets/catawiki/assets/2021/5/3/a/c/9/ac957456-7239-469e-afbb-01c44214492d.jpg')
 *
 * @param imageProxyHost ImageProxy service host url
 * @param imageUrl the url of the lot image to be transformed
 * @param options the transformation options
 */
export const transformImageUrl = (imageProxyHost, imageUrl, options) => {
    try {
        const assetType = 'image';
        const assetPrefix = 'plain/assets/catawiki';
        const originalUrl = new URL(imageUrl);
        const imagePath = originalUrl.pathname
            .replace(/\/thumb\d_|\/original_/g, '/')
            .substring(1);
        const processor = createProcessorQuery(options);
        return [imageProxyHost, assetType, processor, assetPrefix, imagePath].join('/');
    }
    catch {
        return imageUrl;
    }
};
/**
 *
 * Transforms the lot template url using the ImageProxy service (cw-img-proxy).
 * options parameters can be either a known transformation preset or the object
 * that consists of the list of transformations plus width and height
 *
 * @example
 * import { Image } from '@catawiki/runtime-toolkit';
 *
 * const templateUrl = 'https://assets.catawiki.com/image/{{processor}}/plain/assets/catawiki/assets/2022/1/19/0/f/d/0fd698b5-af34-40af-8e2a-ca68adbbbfb7.jpg';
 * assert.strictEqual(Image.transformTemplateUrl(templateUrl, Image.ImageTransformationPreset.LOT_CARD_EXT), 'https://assets.catawiki.com/image/cw_lot_card_ext/plain/assets/catawiki/assets/2022/1/19/0/f/d/0fd698b5-af34-40af-8e2a-ca68adbbbfb7.jpg')
 *
 * @param templateUrl the url of the lot image to be transformed
 * @param options the transformation options
 */
export const transformTemplateUrl = (templateUrl, options) => {
    const processor = createProcessorQuery(options);
    return templateUrl.replace(/{{processor}}/g, processor);
};
